/**
 * Session Header Component
 * Displays session title, back button, and metadata
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '@amzn/meridian/button';
import Link from '@amzn/meridian/link';
import Popover from '@amzn/meridian/popover';
import Icon from '@amzn/meridian/icon';
import exportSmallTokens from '@amzn/meridian-tokens/base/icon/export-small';
import Select, { SelectOption } from '@amzn/meridian/select';
import { Session, updateSessionGroupId, updateSessionMetadata } from '../../../store/sessionSlice';
import { fetchSiteCodes } from '../../../store/siteSlice';
import { useNewSession } from '../../../hooks/useNewSession';
import { useSessionWriteAccess } from '../../../hooks/useSessionWriteAccess';
import { isSessionMergeLocked } from '../../../utils/sessionMergeState';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { showSuccess, showError } from '../../../store/toastsSlice';
import ConsentStatusBadge from './ConsentStatusBadge';
import LinkAustinCaseModal from './LinkAustinCaseModal';
import ValidateSessionModal from './ValidateSessionModal';
import AuditSessionModal from './AuditSessionModal';
import EditableTitle from '../../shared/session/EditableTitle';
import { FONT_SIZE_BODY } from '../../shared/cssConstants';
import { getCreateIncidentUrl, getInitialReportUrl, withSessionHint } from '../../../utils/austin';
import { METADATA_KEYS } from '../../../constants';

interface SessionHeaderProps {
  session?: Session;
}

const SessionHeader: React.FC<SessionHeaderProps> = ({ session }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const startNewSession = useNewSession();
  const { siteCodes, siteCodesStatus } = useAppSelector((state) => state.site);
  const [isEditingSite, setIsEditingSite] = useState(false);
  const [siteSearchQuery, setSiteSearchQuery] = useState('');
  const [isIncidentMenuOpen, setIsIncidentMenuOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [isValidateModalOpen, setIsValidationModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const incidentAnchorRef = useRef<HTMLElement | null>(null);

  const isValidated = session?.validation_state === 'validated';
  const isAuditor = useAppSelector((state) => state.user.isAuditor);
  // Auditors review rather than author, so every write affordance below is off for them.
  const { canEdit } = useSessionWriteAccess(session);
  // Archived and mid-merge sessions are owned by the merge, so they can't be audited either.
  const canAudit = isAuditor && isValidated && !!session && !isSessionMergeLocked(session);

  // Deep-link from the autofill plugin's session list: ?validation=1 auto-opens the validation
  // modal so a reviewer landing here can start/continue the validation immediately. Fire
  // once per navigation and strip the param so a refresh/back doesn't reopen it.
  const validateDeepLinkHandled = useRef(false);
  useEffect(() => {
    if (validateDeepLinkHandled.current) return;
    if (searchParams.get('validation') !== '1') return;
    if (!session) return;
    validateDeepLinkHandled.current = true;
    if (!isValidated && canEdit) setIsValidationModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('validation');
    setSearchParams(next, { replace: true });
  }, [session, isValidated, canEdit, searchParams, setSearchParams]);

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setIsIncidentMenuOpen(false);
  };

  useEffect(() => {
    if (isEditingSite && siteCodesStatus === 'idle') {
      dispatch(fetchSiteCodes());
    }
  }, [isEditingSite, siteCodesStatus, dispatch]);

  const handleSiteChange = async (value: string) => {
    if (!session || !value) return;
    try {
      await dispatch(updateSessionGroupId({ sessionId: session.session_id, groupId: value })).unwrap();
      dispatch(showSuccess('Site updated'));
      setIsEditingSite(false);
    } catch (error: any) {
      dispatch(showError(typeof error === 'string' ? error : 'Failed to update site'));
    }
  };

  // Session name lives in metadata; the title falls back to a default when unset.
  const sessionName = session?.metadata?.[METADATA_KEYS.SESSION_NAME] || '';
  const DEFAULT_SESSION_TITLE = 'Navigator Session';

  const handleNameSave = async (name: string) => {
    if (!session) return;
    try {
      await dispatch(updateSessionMetadata({
        sessionId: session.session_id,
        metadata: { ...(session.metadata || {}), [METADATA_KEYS.SESSION_NAME]: name },
      })).unwrap();
      dispatch(showSuccess('Name updated'));
    } catch (error: any) {
      dispatch(showError(typeof error === 'string' ? error : 'Failed to update name'));
      throw error; // keep the editor open so the user can retry
    }
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleString();
  };

  const searchRegExp = siteSearchQuery ? new RegExp(siteSearchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i') : null;
  const filteredSites = searchRegExp ? siteCodes.filter(c => searchRegExp.test(c)) : siteCodes;

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 40px' }}>
      {/* Row 1: Session Details title + action buttons — only show when viewing an active session */}
      {session && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <EditableTitle
            value={sessionName}
            placeholder={DEFAULT_SESSION_TITLE}
            inputPlaceholder="Enter session name"
            onSave={handleNameSave}
            disabled={!canEdit}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ConsentStatusBadge />
            <Button onClick={startNewSession} size="medium">
              New Session
            </Button>
          </div>
        </div>
      )}

      {/* Row 2: Created / Created By + editable metadata - only show when session exists */}
      {session && (
        <div style={{
          color: '#666',
          fontSize: FONT_SIZE_BODY,
          display: 'flex',
          gap: '24px',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}>
          <span>
            <strong>Created:</strong> {formatDate(session.created_at)}
          </span>
          <span>
            <strong>Created By:</strong> {session.created_by || '--'}
          </span>
          {(
            session.group_id ? (
              <span>
                <strong>Site:</strong> {session.group_id}
              </span>
            ) : isEditingSite ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <strong>Site:</strong>
                <div style={{ width: '160px', display: 'inline-block' }}>
                  <Select
                    value=""
                    onChange={handleSiteChange}
                    searchQuery={siteSearchQuery}
                    onSearch={setSiteSearchQuery}
                    placeholder="Select site"
                    size="small"
                    popoverMaxHeight={200}
                  >
                    {filteredSites.map((code) => (
                      <SelectOption key={code} value={code} label={code} />
                    ))}
                  </Select>
                </div>
                <Button onClick={() => setIsEditingSite(false)} type="link" size="small">
                  Cancel
                </Button>
              </span>
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <strong>Site:</strong> Not set
                <Button
                  onClick={() => setIsEditingSite(true)}
                  type="link"
                  size="small"
                  disabled={!canEdit}
                >
                  Edit
                </Button>
              </span>
            )
          )}
          {session.metadata?.[METADATA_KEYS.AUSTIN_LINK] && session.metadata?.[METADATA_KEYS.CASE_ID] ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <strong>Case ID:</strong>{' '}
              <Link
                href={session.metadata[METADATA_KEYS.AUSTIN_LINK]}
                target="_blank"
                rel="noopener noreferrer"
              >
                {session.metadata[METADATA_KEYS.CASE_ID]}
              </Link>
              <Button
                type="link"
                size="small"
                onClick={() => setIsLinkModalOpen(true)}
                disabled={!canEdit}
              >
                Edit
              </Button>
            </span>
          ) : (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <strong>Case ID:</strong>
              <span>{session.metadata?.[METADATA_KEYS.CASE_ID] || 'Not set'}</span>
              <Button
                type="link"
                size="small"
                onClick={() => setIsLinkModalOpen(true)}
                disabled={!canEdit}
              >
                Edit
              </Button>
              {/* Report Incident: only once validated, no case linked yet, and the viewer may
                  change the session — reporting is not an auditor's job. */}
              {!session.metadata?.[METADATA_KEYS.CASE_ID] && isValidated && canEdit && (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Link
                    ref={incidentAnchorRef}
                    onClick={() => setIsIncidentMenuOpen((open) => !open)}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      Report Incident
                      <Icon tokens={exportSmallTokens} />
                    </span>
                  </Link>
                  <Popover
                    anchorNode={incidentAnchorRef.current ?? undefined}
                    open={isIncidentMenuOpen}
                    onClose={() => setIsIncidentMenuOpen(false)}
                    position="bottom"
                    alignment="start"
                    spacingInset="300"
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => openInNewTab(withSessionHint(getCreateIncidentUrl(), session.session_id))}
                      >
                        Create Incident
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => openInNewTab(withSessionHint(getInitialReportUrl(), session.session_id))}
                      >
                        Create Initial Report Form
                      </Button>
                    </div>
                  </Popover>
                </span>
              )}
            </span>
          )}
          {/* Validate Session: shown until the session is validated, regardless of case link */}
          {!isValidated && (
            <Button
              type="link"
              size="small"
              onClick={() => setIsValidationModalOpen(true)}
              disabled={!canEdit}
            >
              Validate Session
            </Button>
          )}
          {/* Audit Session: for auditors once validated, in every audit state so a finished
              audit can be revisited. */}
          {canAudit && (
            <Button type="link" size="small" onClick={() => setIsAuditModalOpen(true)}>
              {session.audit_state === 'audited' ? 'Review Audit' : 'Audit Session'}
            </Button>
          )}
        </div>
      )}

      {session && (
        <LinkAustinCaseModal
          open={isLinkModalOpen}
          sessionId={session.session_id}
          currentMetadata={session.metadata}
          onClose={() => setIsLinkModalOpen(false)}
        />
      )}

      {session && (
        <ValidateSessionModal
          open={isValidateModalOpen}
          session={session}
          onClose={() => setIsValidationModalOpen(false)}
        />
      )}

      {session && (
        <AuditSessionModal
          open={isAuditModalOpen}
          session={session}
          onClose={() => setIsAuditModalOpen(false)}
        />
      )}
    </div>
  );
};

export default SessionHeader;
